/**
 * The wire shape of a mahjong hand: the snapshot types plus the full-information
 * builder the server feeds them from.
 *
 * `MahjongState` cannot go on the wire as-is: it carries a `Ruleset` object
 * with a `scoreHand` method, the live `wall` / `deadWall` tile arrays, and a
 * `drawnTile` pointer into the current seat's concealed hand. This module is
 * the translation layer — `snapshotFromState` builds the full-information
 * snapshot, which is **server-side only**: the per-seat filter that derives a
 * single recipient's view from it lives next door in `seatView.ts`.
 *
 * Split from `protocol.ts` (and, since T11, split again for the same 200-line
 * cap); `protocol.ts` re-exports both halves, so the client and
 * `packages/game-sessions/src/mahjong/` import from `mahjong/network/protocol` alone.
 */

import type { DiceRollProof } from '@bn-games/verifiable-rng';
import type { Meld, Seat, Tile } from '../game/types.js';
import type { HandResult, MahjongState, Phase } from '../game/stateCore.js';
import type { Ruleset } from '../game/ruleset/types.js';
import { SimplifiedRuleset } from '../game/ruleset/SimplifiedRuleset.js';
import { ClassicalRuleset } from '../game/ruleset/ClassicalRuleset.js';
import type { MatchState } from '../match/MatchState.js';
import { scoreConcludedHand } from '../match/matchScoringContext.js';
import type { HandScore } from '../scoring/types.js';

/**
 * The ruleset travels as an *id*, never as the object — `Ruleset.scoreHand` is
 * a function and JSON has no way to carry it. Both sides resolve the id back
 * to the singleton via `resolveRuleset`. The keys match `settings/types.ts`'s
 * `Variant` literals and each ruleset's own `id`.
 */
export const RULESETS = {
  simplified: SimplifiedRuleset,
  classical: ClassicalRuleset,
} as const satisfies Record<string, Ruleset>;

export type RulesetId = keyof typeof RULESETS;

export function isRulesetId(value: string): value is RulesetId {
  return Object.hasOwn(RULESETS, value);
}

export function resolveRuleset(id: RulesetId): Ruleset {
  return RULESETS[id];
}

/** Narrow a live `Ruleset`'s open-typed `id` back to a wire-safe `RulesetId`. */
export function rulesetIdOf(ruleset: Ruleset): RulesetId {
  if (!isRulesetId(ruleset.id)) throw new Error(`ruleset '${ruleset.id}' has no wire id`);
  return ruleset.id;
}

/**
 * A seat's concealed tiles as the recipient may see them. The recipient's own
 * seat carries `tiles`; every other seat carries only a count, so an opponent's
 * tile ids have nowhere to live in a filtered snapshot.
 */
export type SeatHandView =
  | { readonly kind: 'own'; readonly tiles: readonly Tile[] }
  | { readonly kind: 'hidden'; readonly count: number };

/**
 * The open claim window (server-owned, see the plan's early-out design).
 *
 * `eligibleSeats` is filtered per recipient: *which* seats can claim a discard
 * is derived from their concealed hands, so publishing the full list would leak
 * "seat 2 holds a pair of that tile". A recipient only ever learns whether they
 * themselves are eligible.
 *
 * **How many seats are still deciding is deliberately absent.** An earlier
 * revision carried a `pendingCount` here, reasoning that the window's mere
 * existence already reveals somebody can claim — but that justifies a boolean,
 * not a count. Bots answer in the tick the window opens, so the count that
 * reaches the wire is the number of *humans* who can claim this discard and
 * have not answered, and it was sent to every seat, including seats with no
 * claim of their own who would otherwise learn nothing. Re-broadcasting it on
 * each answer leaked the timing of each opponent's decision too. A discarder
 * seeing "2" would know two opponents were waiting on that tile — which no
 * physical table tells you. It lives on `ClaimWindow.pendingAnswers()` now,
 * server-side only.
 *
 * `deadline` is an absolute epoch-ms timestamp rather than a duration so the
 * two sides never have to agree on when the window opened.
 */
export interface ClaimWindowView {
  readonly eligibleSeats: readonly Seat[];
  readonly deadline: number;
}

export interface MahjongSnapshot {
  readonly rulesetId: RulesetId;
  /**
   * Wall shuffle seed, or `null` while it must stay secret. `buildWall(seed)`
   * reproduces the entire deal, so handing a live seed to a player is handing
   * them every concealed hand — the filter withholds it until the hand is over,
   * at which point it becomes the provenance record. `dealProof` below is
   * withheld on exactly the same terms, since the proof derives this.
   */
  readonly seed: number | null;
  /**
   * The drand round `seed` was derived from in a verifiable room, or `null` in
   * a plain one. **Withheld on exactly the same terms as `seed`**, because it
   * *is* the seed: `deriveMahjongSeed(randomness, roomCode, counter)` reproduces
   * it from this bundle alone, and the seed reproduces every concealed hand. So
   * the filter releases both at `hand-end` and neither before — publishing the
   * proof mid-hand would be publishing the wall.
   */
  readonly dealProof: DiceRollProof | null;
  /** Position-matched with `MahjongState.hands`. */
  readonly handsSeatView: readonly SeatHandView[];
  /**
   * Public by definition — a called set is face-up on the table. A *concealed*
   * kong rides along whole: the solo renderer (`render/HandRenderer.ts`'s
   * `drawMelds`) already draws its outer two tiles face-up and notes that all
   * four copies are identical, so its two face-down tiles carry no information
   * the exposed pair doesn't. Online reveals exactly what solo reveals.
   */
  readonly melds: readonly (readonly Meld[])[];
  readonly discards: readonly (readonly Tile[])[];
  /** Counts only. The wall's *composition* is the set of every unseen tile. */
  readonly wallCount: number;
  readonly deadWallCount: number;
  readonly turn: Seat;
  readonly phase: Phase;
  readonly lastDiscard: { readonly tile: Tile; readonly from: Seat } | null;
  /**
   * The just-drawn tile, already inside `hands[turn]`. Present only in the
   * drawing seat's own view — broadcasting it verbatim would hand every seat
   * the current player's draw.
   */
  readonly drawnTile: Tile | null;
  readonly result: HandResult | null;
  readonly pendingAddedKongSeat: Seat | null;
  /** `null` for Simplified rooms, which have no match layer. */
  readonly matchState: MatchState | null;
  /**
   * The concluded hand's score — plain data (points + matched pattern names),
   * so unlike `Ruleset` it travels as itself. Non-`null` only at `hand-end` in
   * a scoring match: a live hand has no `result` to score, so this can never
   * describe tiles still in play, and what it does describe is the *winner's*
   * hand, which hand-end reveals anyway. Public — no filtering needed.
   */
  readonly handScore: HandScore | null;
  readonly claimWindow: ClaimWindowView | null;
}

/**
 * Build the full-information snapshot. Server-side only — every recipient gets
 * `filterSnapshotForSeat` applied to the result before it reaches a socket.
 */
export function snapshotFromState(
  state: MahjongState,
  matchState: MatchState | null = null,
  claimWindow: ClaimWindowView | null = null,
  dealProof: DiceRollProof | null = null,
): MahjongSnapshot {
  return {
    rulesetId: rulesetIdOf(state.ruleset),
    seed: state.seed,
    dealProof,
    handsSeatView: state.hands.map((tiles) => ({ kind: 'own', tiles })),
    melds: state.melds,
    discards: state.discards,
    wallCount: state.wall.length,
    deadWallCount: state.deadWall.length,
    turn: state.turn,
    phase: state.phase,
    lastDiscard: state.lastDiscard,
    drawnTile: state.drawnTile,
    result: state.result,
    pendingAddedKongSeat: state.pendingAddedKongSeat,
    matchState,
    // Derived rather than passed in: the score is a pure function of the hand
    // that just ended, so computing it where the hand goes on the wire leaves
    // no way for a caller to publish a stale one.
    handScore: scoreFor(state, matchState),
    claimWindow,
  };
}

/** `null` outside `hand-end`, under Simplified (no match, no scoring ruleset),
 *  and for an exhausted hand — `scoreConcludedHand` covers the last two. */
function scoreFor(state: MahjongState, matchState: MatchState | null): HandScore | null {
  if (matchState === null || state.phase !== 'hand-end' || state.result === null) return null;
  return scoreConcludedHand(matchState, state, state.result, state.ruleset);
}
